<template>
  <b-modal ref="modal" :title="text.title" size="xl" scrollable ok-only :ok-title="text.close" ok-variant="secondary">
    <div class="chart-guide__intro">
      <p class="chart-guide__purpose">{{ text.purpose }}</p>
      <div class="chart-guide__lang">
        <button
          v-for="option in languages"
          :key="option.value"
          type="button"
          class="btn btn-sm"
          :class="lang === option.value ? 'btn-mantraz' : 'btn-outline-secondary'"
          @click="setLang(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <section class="chart-guide__section">
      <h6 class="chart-guide__heading">{{ text.compareTitle }}</h6>
      <div class="chart-guide__cards">
        <div v-for="(item, i) in text.compare" :key="item.title" class="chart-guide__card">
          <i class="chart-guide__icon" :class="compareIcons[i]"></i>
          <div class="chart-guide__card-title">{{ item.title }}</div>
          <div class="chart-guide__card-body">{{ item.body }}</div>
        </div>
      </div>
      <figure class="chart-guide__figure">
        <img :src="`${imgBase}img/custom-chart/guide-chart.jpeg`" :alt="text.figureCompare" loading="lazy">
        <figcaption>{{ text.figureCompare }}</figcaption>
      </figure>
    </section>

    <section class="chart-guide__section chart-guide__split">
      <div>
        <h6 class="chart-guide__heading">{{ text.stepsTitle }}</h6>
        <ol class="chart-guide__steps">
          <li v-for="(step, i) in text.steps" :key="step">
            <span class="chart-guide__step-no">{{ i + 1 }}</span>
            <span>{{ step }}</span>
          </li>
        </ol>
      </div>
      <figure class="chart-guide__figure">
        <img :src="`${imgBase}img/custom-chart/guide-form.jpeg`" :alt="text.figureForm" loading="lazy">
        <figcaption>{{ text.figureForm }}</figcaption>
      </figure>
    </section>

    <section class="chart-guide__section">
      <h6 class="chart-guide__heading">{{ text.tipsTitle }}</h6>
      <ul class="chart-guide__tips">
        <li v-for="(tip, i) in text.tips" :key="tip">
          <i :class="tipIcons[i]"></i>
          <span>{{ tip }}</span>
        </li>
      </ul>
    </section>
  </b-modal>
</template>

<script>
const LANG_KEY = 'customChartGuideLang';

const GUIDE = {
  en: {
    title: 'How to use Custom Chart',
    close: 'Close',
    purpose: 'Build your own charts from vessel sensor data and compare them side by side.',
    compareTitle: 'Three ways to compare',
    compare: [
      { title: 'Different data, one vessel', body: 'e.g. RPM, fuel flow and speed of one vessel this week. Each unit gets its own Y axis.' },
      { title: 'Vessel vs vessel', body: 'e.g. fuel flow of two vessels over the same period.' },
      { title: 'Period vs period', body: 'e.g. this week vs last week, or before vs after a repair. The other period is laid over the main range, and the tooltip shows its real date.' },
    ],
    stepsTitle: 'Steps',
    steps: [
      'Click Create Chart.',
      'Name the chart, then pick a range and an interval (Auto is recommended).',
      'Click Add series and choose the vessel, device, data point and period for each line.',
      'Click Save. The chart appears as a card you can refresh, edit, delete, resize or drag to reorder.',
    ],
    tipsTitle: 'Tips',
    tips: [
      'Up to 8 series and 4 units per chart.',
      'Drag across the chart to zoom; double-click or use Reset zoom to go back.',
      'Lock Y axis keeps the scale fixed while you compare.',
      'PNG and CSV export exactly what you see.',
      'Charts are saved in this browser only.',
    ],
    figureCompare: 'Example: two vessels, this week vs last week.',
    figureForm: 'The chart form: range, interval and one row per series.',
  },
  id: {
    title: 'Cara pakai Custom Chart',
    close: 'Tutup',
    purpose: 'Buat grafik sendiri dari data sensor kapal dan bandingkan berdampingan.',
    compareTitle: 'Tiga cara membandingkan',
    compare: [
      { title: 'Beda data, satu kapal', body: 'misalnya RPM, aliran fuel, dan kecepatan satu kapal minggu ini. Tiap satuan punya sumbu Y sendiri.' },
      { title: 'Kapal vs kapal', body: 'misalnya aliran fuel dua kapal di periode yang sama.' },
      { title: 'Periode vs periode', body: 'misalnya minggu ini vs minggu lalu, atau sebelum vs sesudah perbaikan. Periode lain ditumpuk di atas rentang utama, dan tooltip menampilkan tanggal aslinya.' },
    ],
    stepsTitle: 'Langkah',
    steps: [
      'Klik Create Chart.',
      'Beri nama grafik, lalu pilih rentang dan interval (Auto disarankan).',
      'Klik Add series, lalu pilih kapal, device, data, dan periode untuk tiap garis.',
      'Klik Save. Grafik muncul sebagai kartu yang bisa di-Refresh, Edit, Delete, diubah lebarnya, atau digeser urutannya.',
    ],
    tipsTitle: 'Tips',
    tips: [
      'Maksimal 8 seri dan 4 satuan per grafik.',
      'Seret di grafik untuk zoom; klik dua kali atau Reset zoom untuk kembali.',
      'Lock Y axis mengunci skala saat membandingkan.',
      'PNG dan CSV mengunduh persis apa yang sedang tampil.',
      'Grafik tersimpan di browser ini saja.',
    ],
    figureCompare: 'Contoh: dua kapal, minggu ini vs minggu lalu.',
    figureForm: 'Form grafik: rentang, interval, dan satu baris per seri.',
  },
};

function readLang() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    return GUIDE[saved] ? saved : 'en';
  } catch (e) {
    return 'en';
  }
}

export default {
  name: 'ChartGuideModal',
  data() {
    return {
      lang: readLang(),
      languages: [
        { value: 'en', label: 'English' },
        { value: 'id', label: 'Bahasa Indonesia' },
      ],
      imgBase: process.env.BASE_URL,
      compareIcons: ['ri-bar-chart-2-line', 'ri-ship-line', 'ri-calendar-2-line'],
      tipIcons: ['ri-stack-line', 'ri-zoom-in-line', 'ri-lock-line', 'ri-download-2-line', 'ri-computer-line'],
    };
  },
  computed: {
    text() {
      return GUIDE[this.lang] || GUIDE.en;
    },
  },
  methods: {
    open() {
      this.$refs.modal.show();
    },
    setLang(lang) {
      this.lang = lang;
      try {
        localStorage.setItem(LANG_KEY, lang);
      } catch (e) {
        // Browser blok storage: pilihan cuma berlaku sesi ini.
      }
    },
  },
};
</script>

<style scoped>
.chart-guide__intro { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.chart-guide__purpose { margin: 0; font-size: 15px; color: #374151; }
.chart-guide__lang { display: flex; gap: 6px; flex-shrink: 0; }
.chart-guide__section { margin-top: 16px; padding-top: 16px; border-top: 1px solid #eef0f3; }
.chart-guide__heading { margin-bottom: 10px; font-size: 13px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; color: #6b7280; }
.chart-guide__cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.chart-guide__card { padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb; }
.chart-guide__icon { font-size: 20px; color: #ff69b4; }
.chart-guide__card-title { margin: 6px 0 4px; font-size: 14px; font-weight: 700; color: #111827; }
.chart-guide__card-body { font-size: 13px; color: #4b5563; }
.chart-guide__figure { margin: 14px 0 0; text-align: center; }
.chart-guide__figure img { max-width: 100%; max-height: 340px; object-fit: contain; border: 1px solid #e5e7eb; border-radius: 6px; }
.chart-guide__figure figcaption { margin-top: 4px; font-size: 12px; color: #6b7280; }
.chart-guide__split { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); gap: 20px; align-items: start; }
.chart-guide__split .chart-guide__figure { margin-top: 0; }
.chart-guide__steps { margin: 0; padding: 0; list-style: none; }
.chart-guide__steps li { display: flex; gap: 10px; margin-bottom: 10px; font-size: 13px; color: #374151; }
.chart-guide__step-no { display: flex; flex: 0 0 24px; align-items: center; justify-content: center; height: 24px; border-radius: 50%; background: #ff69b4; color: #fff; font-size: 12px; font-weight: 700; }
.chart-guide__tips { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 20px; margin: 0; padding: 0; list-style: none; }
.chart-guide__tips li { display: flex; gap: 8px; font-size: 13px; color: #374151; }
.chart-guide__tips i { font-size: 16px; color: #6b7280; }

/* Layar sempit: semua jadi satu kolom. */
@media (max-width: 991px) {
  .chart-guide__intro { flex-direction: column; }
  .chart-guide__cards,
  .chart-guide__split,
  .chart-guide__tips { grid-template-columns: minmax(0, 1fr); }
}
</style>
